import React, { useState } from "react";
import { useSelector } from "react-redux";
import ListingModalComments from "../listingModalComments";
import ReactDOMServer from "react-dom/server";
import { Tooltip } from "reactstrap";
import classNames from "classnames";
import OpenHouse from "../openHouse";
import AgentBusinessCardMini from "../AgentBusinessCardMini";
import styled from "styled-components";

// begin images
import nophoto from "../../images/icons/nophoto.svg";
import { ReactComponent as ImageCount } from "../../images/icons/details-report/image-count.svg";
import { ReactComponent as VirtualTour } from "../../images/icons/details-report/virtualtour.svg";
import { ReactComponent as VirtualTourInteractive } from "../../images/icons/details-report/streetview.svg";
import { ReactComponent as StreetView } from "../../images/icons/details-report/streetview.svg";
import { ReactComponent as ThreeDTour } from "../../images/icons/details-report/3dtour.svg";
import { ReactComponent as BirdsEyeView } from "../../images/icons/details-report/birdseyeview.svg";
import { ReactComponent as Open } from "../../images/icons/details-report/open.svg";
import { ReactComponent as Percent } from "../../images/icons/details-report/percent.svg";
import { ReactComponent as OtherMedia3 } from "../../images/icons/details-report/othermedia3.svg";
import { ReactComponent as Tour } from "../../images/icons/details-report/tourcam.svg";
import { ReactComponent as LiveStream } from "../../images/icons/details-report/livestream.svg";
import { getToken } from "../../utils";
import AddComment from "../addComment";

const ReportDetailsImage = ({
  listing,
  onClick,
  buildOpenHouseBar,
  publicPortal,
  onContactAgent,
  listingDetails = null,
  showNotification,  
  shareLink
}) => {
  const activities = useSelector((state) => state.entities.Activities);
  const myProfile = useSelector((state) => state.profile.mine);
  const [tooltipOpen, setTooltipOpen] = useState({});

  const toggle = (type) => setTooltipOpen(oldValues => ({...oldValues, [type]: !oldValues[type]}));

  const buildMediaLinks = () => {
    return listing.MediaLinks?.map((link) => (
      <span
        key={link.type}
        onClick={() =>
          window.open(
            link.url,
            link.type,
            "height=640,width=1000,scrollbars=1,menubar=1,resizable=1"
          )
        }
      >
        <span id={`${link.type}-tooltip`} className="d-inline-flex">
          {getSVG(link.type)} {link.label}
        </span>
        <Tooltip
          placement="top"
          delay={{ show: 0, hide: 0 }}
          isOpen={tooltipOpen[link.type]}
          target={`${link.type}-tooltip`}
          toggle={() => toggle(link.type) }
        >
          {link.hovertext}
        </Tooltip>
      </span>
    ));
  };

  return (
    <div className="reports-detail-image row">
      <div className="col-lg-6">
        <div
          className={classNames("photo-container", {
            "with-open-houses": listing.OpenHouses,
          })}
        >
          <img
            style={{ width: "100%", cursor: "pointer" }}
            src={(
              (listing.ListingPhotosFullsize &&
                listing.ListingPhotosFullsize[0]) ||
              nophoto
            )}
            alt="listing"
            onClick={onClick}
          />
          {buildOpenHouseBar(listing)}
          <div className={"listing_folder_flag " + listing.MlsStatus}>
            {/* style={{ color: listing.StatusColor }}> */}
            {listing.MlsStatus}
          </div>
          <div className="image-count">
            <ImageCount />
            View {listing.ListingPhotosFullsize.length}
          </div>
        </div>
        {listing?.MediaLinks?.length > 0 && (
          <div className="tools-icons">{buildMediaLinks()}</div>
        )}
        <div>
          {listing.OpenHouses && <OpenHouse OpenHouses={listing.OpenHouses} />}
        </div>
      </div>
      {/* max height is a hack to keep the comments box from coming apart */}
      <div
        className="col-lg-6">
        {listingDetails}
        {publicPortal ? (
          <AgentBusinessCardMini shareLink={shareLink} withKnowMore onContactAgent={onContactAgent} />
        ) : (
          <div className="mb-4">
            <div className="discuss-listing">
              Discuss Listing with your Agent
            </div>
            <div className="activity activity_overflow_auto mb-0">
              <ListingModalComments
                listing={listing}
                comments={
                  activities[listing.ActivityId]
                    ? activities[listing.ActivityId].Comments
                    : null
                }
                myProfile={myProfile}
                hideAddComment
              />
            </div>
            <div className="activity position-relative">
              <AddComment 
                id={listing.ActivityId} 
                propertyId={listing.Id} 
                myProfile={myProfile}
                showNotification={showNotification}
                className="bottom_fix left-0 width-100%"                
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDetailsImage;

// these functions are no longer used in this component but used in details report
export const replaceFontAwesome = (
  section,
  hideIfNoIcon,
  withLinkText,
  connectMLSUrl
) => {
  if (!section.html) return null; // TODO how to handle Virtual Tour

  const element = htmlToElement(section.html);
  let parent = null;
  const iTag = element.getElementsByTagName("i")[0];
  const imageTag = element.getElementsByTagName("img")[0];
  if (iTag) {
    parent = iTag.parentNode;
    parent.removeChild(iTag);
  } else if (imageTag) {
    parent = imageTag.parentNode;
    parent.removeChild(imageTag);
  }

  const icon = getSVG(section.name);
  if (icon) {
    const span = document.createElement("span");
    span.className = "d-inline-flex";
    span.innerHTML = ReactDOMServer.renderToString(icon);
    if (withLinkText) parent.prepend(span);
    else parent.innerHTML = span.outerHTML;
  } else if (hideIfNoIcon) return null;

  if (connectMLSUrl) {
    if (element.href)
      element.href = appendURLandToken(element.href, connectMLSUrl);
    else
      element.getElementsByTagName("a")[0].href = appendURLandToken(
        element.getElementsByTagName("a")[0].href,
        connectMLSUrl
      );
  }

  return element.outerHTML;
};

// prepends connectMLS url to relative links and appends token to query string
const appendURLandToken = (href, url) => {
  let regexRes;
  let result = href;
  const regex = /openWindow\(['"]*(.*?)(\s*['"]*,.*?)/gm;

  while ((regexRes = regex.exec(href)) !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (regexRes.index === regex.lastIndex) {
      regex.lastIndex++;
    }
    // eslint-disable-next-line
    regexRes.forEach((match, groupIndex) => {
      if (groupIndex === 1 && match[0] === "/") {
        result = href.replace(match, `${url}${appendTokenDetails(match)}`);
      }
    });
  }
  return result;
};

export const appendTokenDetails = (url) => {
  if (!url) return null;

  const index = url.indexOf("%27");
  const character = url.includes("?") ? "&" : "?";

  const token = getToken();

  return index !== -1
    ? `${url.slice(0, index)}${character}token=${token}${url.slice(index)}`
    : `${url}${character}token=${token}`;
};

const VTInteractiveContainer = styled.div`
  svg {
    path {
      fill: ${({ fillColor }) => fillColor};
    }
  }
`;

function VTInteractivePathColorChange({ fillColor }) {
  return (
    <VTInteractiveContainer fillColor={fillColor}>
      <VirtualTourInteractive />
    </VTInteractiveContainer>
  );
}

const getSVG = (name) => {
  switch (name) {
    case "VirtualTour":
    case "Virtual Tour":
      return <VirtualTour />;
    case "Interactive Virtual Tour":
      return <VTInteractivePathColorChange fillColor="#E57A77"/>;
    case "VirtualTourInteractive":
      return <VTInteractivePathColorChange fillColor="#E57A77"/>;
    case "VirtualTourUnbranded":
      return <VirtualTour />;
    case "aerialLink":
      return <StreetView />;
    case "birdseyeView":
      return <BirdsEyeView />;
    case "calcLink":
      return <Percent />;
    case "Other":
      return <OtherMedia3 />;
    case "Video":
      return <Tour />;
    case "3DTour":
      return <ThreeDTour />;
    case "3D Tour":
      return <ThreeDTour />;
    case "livestream":
      return <LiveStream />;
    default:
      return <Open />;
  }
};

const htmlToElement = (html) => {
  const template = document.createElement("template");
  html = html.trim(); // Never return a text node of whitespace as the result
  template.innerHTML = html;
  return template.content.firstChild;
};
